const POST_AUTH_DESTINATION_KEY = "ledger.auth.postAuthDestination";

function safeDestination(value: string | null): string | null {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return null;
  if (value.startsWith("/login") || value.startsWith("/callback")) return null;
  return value;
}

/** Remember the in-app route that sent an unauthenticated user to sign-in. */
export function rememberPostAuthDestination(): void {
  if (typeof window === "undefined") return;
  const destination = safeDestination(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
  );
  if (destination) rememberPostAuthDestinationValue(destination);
}

/** Remember an explicit safe in-app route (used by public entry points). */
export function rememberPostAuthDestinationValue(value: string): void {
  if (typeof window === "undefined") return;
  const destination = safeDestination(value);
  if (!destination) return;
  try {
    window.localStorage.setItem(POST_AUTH_DESTINATION_KEY, destination);
  } catch {
    // Storage can be unavailable in privacy modes. Login still works; only the
    // convenience return path is lost.
  }
}

/** Inspect the pending destination without consuming it (used for contextual sign-in). */
export function peekPostAuthDestination(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return safeDestination(window.localStorage.getItem(POST_AUTH_DESTINATION_KEY));
  } catch {
    return null;
  }
}

/** Abandon a pending intent when the reviewer explicitly returns to the guide. */
export function clearPostAuthDestination(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(POST_AUTH_DESTINATION_KEY);
  } catch {
    // Nothing else is required when storage is unavailable.
  }
}

/** Read and clear the remembered route so an old intent cannot fire later. */
export function consumePostAuthDestination(): string {
  if (typeof window === "undefined") return "/";
  try {
    const destination = safeDestination(
      window.localStorage.getItem(POST_AUTH_DESTINATION_KEY),
    );
    window.localStorage.removeItem(POST_AUTH_DESTINATION_KEY);
    return destination ?? "/";
  } catch {
    return "/";
  }
}

/** Reload once under the new token so route guards and entity queries agree. */
export function continueAfterAuthentication(): void {
  window.location.assign(consumePostAuthDestination());
}
